import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { buildKiotHeaderIconButtonSx } from '@/components/layout/banhangKiotTokens'
import { useI18n } from '@/contexts/I18nContext'
import { tokenStorage } from '@/services/tokenStorage'
import {
  deleteThongBao,
  getThongBaoList,
  markAllThongBaoRead,
  type ThongBaoMoi,
} from '@/apis/thongBaoApi'
import { showMessage } from '@/utils/commonUtils'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { useThongBaoRealtime } from '@/hooks/useThongBaoRealtime'
import { VAN_HANH_PERMISSION_CODES } from '@/constants/chucNangs/vanHanhChucNangs'
import { NHOM_THONG_BAO, duongDanThongBao, type NhomThongBao } from '@/constants/thongBaoConstants'
import routerConstants from '@/constants/routerConstants'
import { notificationService } from '@/services/notificationService'
import type { ThongBaoRealtime } from '@/services/thongBaoRealtime'
import {
  docCaiDatTrinhDuyet,
  hienThongBaoHeThong,
  phatAmThanhThongBao,
} from '@/services/thongBaoTrinhDuyet'
import ThongBaoItemRow from './ThongBaoItemRow'
import { thongBaoToastStyle } from './thongBaoUiUtils'

const PAGE_SIZE = 15
const PANEL_WIDTH = 460
const XANH = '#1877F2'

const tabSx = {
  flexShrink: 0,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    margin: 0,
    '&:not(:first-of-type)': { border: 'none', marginLeft: 0 },
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    borderRadius: '14px !important',
    px: 1.1,
    py: 0,
    minHeight: 28,
    fontSize: '0.8rem',
    fontWeight: 600,
    border: 'none',
    color: 'text.secondary',
    '&.Mui-selected': { bgcolor: 'rgba(24, 119, 242, 0.12)', color: XANH, fontWeight: 700 },
  },
} as const

const chipSx = {
  flexWrap: 'wrap',
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    margin: 0,
    '&:not(:first-of-type)': { border: 'none', marginLeft: 0 },
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    borderRadius: '12px !important',
    px: 1,
    py: 0,
    height: 24,
    fontSize: '0.75rem',
    border: 'none',
    bgcolor: 'rgba(0, 0, 0, 0.04)',
    whiteSpace: 'nowrap',
    '&.Mui-selected': { bgcolor: 'rgba(24, 119, 242, 0.12)', color: XANH, fontWeight: 700 },
  },
} as const

const nutHeaderSx = {
  textTransform: 'none',
  fontSize: '0.75rem',
  fontWeight: 600,
  minWidth: 0,
  px: 0.75,
  py: 0.25,
  lineHeight: 1.2,
  color: XANH,
} as const

type Tab = 'all' | 'unread'

/**
 * Chuông thông báo header (giao diện theo YTL) — danh sách /thong-bao + realtime Socket.IO:
 * thông báo mới → tăng badge, chèn đầu danh sách; popup màu theo loại / âm thanh /
 * thông báo hệ điều hành chỉ khi loại đó đang bật trong cài đặt (hienPopup).
 */
const InAppNotificationMenu: React.FC<{ headerMode?: boolean }> = ({ headerMode = false }) => {
  const { resource, common: commonT, locale } = useI18n()
  const t = useCallback((k: string, p?: Record<string, string | number>) => resource('thongBao', k, p), [resource])
  const theme = useTheme()
  const navigate = useNavigate()
  const { hasChucNangByCode } = useMenuChucNang()
  const canView = hasChucNangByCode(VAN_HANH_PERMISSION_CODES.WEB_BH_THONG_BAO)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [panelMaxHeight, setPanelMaxHeight] = useState<number>()
  const [tab, setTab] = useState<Tab>('all')
  const [nhom, setNhom] = useState<NhomThongBao | ''>('')
  const [rows, setRows] = useState<ThongBaoMoi[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [chuaDoc, setChuaDoc] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const daNhanRef = useRef(new Set<string>())

  const userType = tokenStorage.getUserType()
  const enabled = canView && userType === 'user'
  const open = Boolean(anchorEl)
  const hasMore = rows.length < total

  const taiSoChuaDoc = useCallback(async () => {
    if (!enabled) return
    try {
      const res = await getThongBaoList('page=1&pageSize=1')
      setChuaDoc(res.chuaDoc)
    } catch {
      setChuaDoc(0)
    }
  }, [enabled])

  const taiDanhSach = useCallback(
    async (trang = 1) => {
      if (!enabled) return
      const qs = new URLSearchParams({ page: String(trang), pageSize: String(PAGE_SIZE) })
      if (tab === 'unread') qs.set('watched', 'false')
      if (nhom) qs.set('nhom', nhom)
      if (trang === 1) setLoading(true)
      else setLoadingMore(true)
      try {
        const res = await getThongBaoList(qs.toString())
        setRows((cu) => (trang === 1 ? res.data : [...cu, ...res.data.filter((r) => !cu.some((x) => x.id === r.id))]))
        setTotal(res.paging.total)
        setPage(trang)
        setChuaDoc(res.chuaDoc)
      } catch {
        if (trang === 1) setRows([])
        showMessage('error', t('loadError'))
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [enabled, tab, nhom, t],
  )

  useEffect(() => {
    void taiSoChuaDoc()
  }, [taiSoChuaDoc])

  useEffect(() => {
    if (open) void taiDanhSach(1)
  }, [open, taiDanhSach])

  const loaiLabel = useCallback(
    (ma: string) => {
      const label = t(`loai.${ma}`)
      return label === `resources.thongBao.loai.${ma}` ? '' : label
    },
    [t],
  )

  const moThongBao = useCallback(
    (tb: Pick<ThongBaoMoi, 'loaiThongBao' | 'refId' | 'title'>) => {
      navigate(duongDanThongBao(tb) ?? routerConstants.thongBaoTrungTam)
    },
    [navigate],
  )

  const onThongBaoMoi = useCallback(
    (tb: ThongBaoRealtime) => {
      if (daNhanRef.current.has(tb.id)) return
      daNhanRef.current.add(tb.id)
      setChuaDoc((c) => c + 1)
      if (!nhom || nhom === tb.nhom) {
        setRows((cu) => (cu.some((r) => r.id === tb.id) ? cu : [tb, ...cu]))
        setTotal((n) => n + 1)
      }
      if (!tb.hienPopup) return

      const caiDat = docCaiDatTrinhDuyet()
      if (caiDat.amThanh) phatAmThanhThongBao()
      if (caiDat.heThong && document.hidden) {
        hienThongBaoHeThong(tb.title, tb.content, tb.id, () => moThongBao(tb))
      }
      notificationService.info(`${tb.title} — ${tb.content}`, {
        duration: 10000,
        position: { vertical: 'bottom', horizontal: 'right' },
        style: thongBaoToastStyle(tb.loaiThongBao),
        preventDuplicate: false,
        action: { label: t('xem'), onClick: () => moThongBao(tb) },
      })
    },
    [moThongBao, nhom, t],
  )

  useThongBaoRealtime(enabled, {
    onMoi: onThongBaoMoi,
    onCapNhat: () => {
      if (open) void taiDanhSach(1)
      else void taiSoChuaDoc()
    },
  })

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setPanelMaxHeight(Math.max(320, Math.floor(window.innerHeight - rect.bottom - 16)))
    setAnchorEl(e.currentTarget)
  }

  const handleClickItem = async (tb: ThongBaoMoi) => {
    if (!tb.watched) {
      setRows((cu) => cu.map((r) => (r.id === tb.id ? { ...r, watched: true } : r)))
      setChuaDoc((c) => Math.max(0, c - 1))
      markAllThongBaoRead(tb.id).catch(() => void taiDanhSach(1))
    }
    if (tb.refId) {
      setAnchorEl(null)
      moThongBao(tb)
    }
  }

  const handleDocHet = async () => {
    if (busy) return
    setBusy(true)
    try {
      await markAllThongBaoRead()
      setRows((cu) => cu.map((r) => ({ ...r, watched: true })))
      setChuaDoc(0)
      if (tab === 'unread') void taiDanhSach(1)
    } catch (err) {
      showMessage('error', (err as { message?: string })?.message || commonT('messages.error'))
    } finally {
      setBusy(false)
    }
  }

  const handleXoa = async (e: React.MouseEvent, tb: ThongBaoMoi) => {
    e.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      await deleteThongBao(tb.id)
      setRows((cu) => cu.filter((r) => r.id !== tb.id))
      setTotal((n) => Math.max(0, n - 1))
      if (!tb.watched) setChuaDoc((c) => Math.max(0, c - 1))
    } catch (err) {
      showMessage('error', (err as { message?: string })?.message || commonT('messages.error'))
    } finally {
      setBusy(false)
    }
  }

  if (userType !== 'user') return null

  const headerIconSx = {
    ...buildKiotHeaderIconButtonSx({
      activeBg: theme.palette.banhang.navItemActiveBg,
      activeBgHover: theme.palette.banhang.navItemActiveBgHover,
    }),
    height: '100%',
    minHeight: 0,
    alignSelf: 'stretch',
  }

  return (
    <>
      <IconButton
        size={headerMode ? 'small' : 'medium'}
        color="inherit"
        aria-label={t('ariaLabel')}
        onClick={handleOpen}
        sx={
          headerMode
            ? headerIconSx
            : {
                border: '1px solid rgba(255, 255, 255, 0.22)',
                borderRadius: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
              }
        }
      >
        <Badge badgeContent={chuaDoc} color="error" max={99} invisible={chuaDoc === 0}>
          {headerMode ? <NotificationsNoneOutlinedIcon sx={{ fontSize: 18 }} /> : <NotificationsOutlinedIcon />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: PANEL_WIDTH,
              maxWidth: 'calc(100vw - 16px)',
              maxHeight: panelMaxHeight ?? 'calc(100dvh - 80px)',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              mt: 0.75,
              boxShadow: '0 12px 28px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: '1.15rem', mr: 0.5, lineHeight: 1.2 }}>
              {t('title')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tab}
              onChange={(_e, v: Tab | null) => v && setTab(v)}
              sx={tabSx}
            >
              <ToggleButton value="all">{t('filterAll')}</ToggleButton>
              <ToggleButton value="unread">
                {t('filterUnread')}
                {chuaDoc > 0 ? ` (${chuaDoc})` : ''}
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <Button
                size="small"
                onClick={() => void handleDocHet()}
                disabled={busy || chuaDoc === 0}
                sx={nutHeaderSx}
                title={t('markAllRead')}
              >
                {t('markAllReadShort')}
              </Button>
              <Tooltip title={t('caiDat')}>
                <IconButton
                  size="small"
                  aria-label={t('caiDat')}
                  onClick={() => {
                    setAnchorEl(null)
                    navigate(routerConstants.settingsThongBao)
                  }}
                >
                  <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={nhom || 'all'}
            onChange={(_e, v: string | null) => {
              if (v != null) setNhom(v === 'all' ? '' : (v as NhomThongBao))
            }}
            sx={chipSx}
          >
            <ToggleButton value="all">{t('filterAll')}</ToggleButton>
            {Object.values(NHOM_THONG_BAO).map((ma) => (
              <ToggleButton key={ma} value={ma}>
                {t(`phanHe.${ma}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 280 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.disabled',
                }}
              >
                <NotificationsOffOutlinedIcon />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('empty')}
              </Typography>
            </Box>
          ) : (
            <>
              {rows.map((tb) => (
                <ThongBaoItemRow
                  key={tb.id}
                  item={tb}
                  loaiLabel={loaiLabel(tb.loaiThongBao)}
                  locale={locale}
                  xemLabel={t('xem')}
                  xoaLabel={t('xoa')}
                  coChiTiet={Boolean(tb.refId)}
                  onClick={() => void handleClickItem(tb)}
                  onDelete={(e) => void handleXoa(e, tb)}
                  deleteDisabled={busy}
                />
              ))}
              {hasMore ? (
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => void taiDanhSach(page + 1)}
                    disabled={loadingMore}
                    sx={{ textTransform: 'none', fontWeight: 600, color: XANH }}
                  >
                    {loadingMore ? <CircularProgress size={18} /> : t('loadMore')}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setAnchorEl(null)
              navigate(routerConstants.thongBaoTrungTam)
            }}
            sx={{ textTransform: 'none', fontWeight: 700, color: XANH }}
          >
            {t('xemTatCa')}
          </Button>
        </Box>
      </Popover>

    </>
  )
}

export default InAppNotificationMenu
